import { lazy } from 'react';

// project imports
import AuthGuard from 'utils/route-guard/AuthGuard';
import MainLayout from 'layout/MainLayout';
import Loadable from 'ui-component/Loadable';
import MessagingGuard from 'utils/route-guard/MessagingGuard';
import AdminArchivedDonorScreen from 'views/admin/donor-section/archived-users/admin-archived-donor-screen';
import AdminArchivedRecipientScreen from 'views/admin/recipient-section/archived-users/admin-archived-recipient-screen';
import AdminArchivedDeliveryDriverScreen from 'views/admin/delivery-driver-section/archived-users/admin-archived-delivery-driver-screen';
import AdminArchivedAdministratorScreen from 'views/admin/organization/administrator-section/archived-users/admin-archived-administrator-screen';
import AdminArchivedRoutesScreen from 'views/admin/routes-section/archived-users/admin-archived-routes-screen';

// main page routing
const Dashboard = Loadable(lazy(() => import('views/admin/dashboard')));
const PhotosPage = Loadable(lazy(() => import('views/admin/photos')));
const AdminMetricsPage = Loadable(lazy(() => import('views/admin/metrics/AdminMetrics')));
const RoutesPage = Loadable(lazy(() => import('views/admin/routes-section/view-routes')));
const RoutesCreatePage = Loadable(lazy(() => import('views/admin/routes-section/create-routes')));
const RoutesEditPage = Loadable(lazy(() => import('views/admin/routes-section/edit-routes')));
const AchievementsPage = Loadable(lazy(() => import('views/admin/achievements-section/admin-achievements-screen')));
const AchievementPage = Loadable(lazy(() => import('views/admin/achievements-section/admin-achievement-screen')));
const PrizesPage = Loadable(lazy(() => import('views/admin/prizes/PrizesPage')));
const PrizePage = Loadable(lazy(() => import('views/admin/prizes/update-prize')));
const PendingRoutesPage = Loadable(lazy(() => import('views/admin/routes-section/pending-routes')));
const MarketplaceRoutesPage = Loadable(lazy(() => import('views/admin/routes-section/marketplace-routes')));
const RouteInstancesByDatePage = Loadable(lazy(() => import('views/admin/routes-section/view-route-instances-by-date')));
const RouteReportPage = Loadable(lazy(() => import('views/admin/reports-section/report-route')));
const ProfileReportPage = Loadable(lazy(() => import('views/admin/reports-section/report-profile')));
const TemplatesPage = Loadable(lazy(() => import('views/admin/reports-section/report-templates')));
const TemplateHelpPage = Loadable(lazy(() => import('views/admin/reports-section/report-templates/TemplateHelpPage')));

const ProgramsPage = Loadable(lazy(() => import('views/admin/programs-section/admin-programs-screen')));
const ArchivedProgramsPage = Loadable(lazy(() => import('views/admin/programs-section/admin-archived-programs-screen')));
const ProgramPage = Loadable(lazy(() => import('views/admin/programs-section/admin-program-screen')));


//organization
const OrganizationSettingsPage = Loadable(lazy(() => import('views/admin/organization/organization-settings')));
const AdministratorsPage = Loadable(lazy(() => import('views/admin/organization/administrator-section/view-users')));
const AdministratorUpdatePage = Loadable(lazy(() => import('views/admin/organization/administrator-section/update-user')));
const FoodProductsPage = Loadable(lazy(() => import('views/admin/organization/food-products-section/view-food-products/FoodProductsPage')));
const ArchivedFoodProductsPage = Loadable(lazy(() => import('views/admin/organization/food-products-section/view-food-products/ArchivedFoodProductsPage')));

//donor
const DonorsPage = Loadable(lazy(() => import('views/admin/donor-section/view-users')));
const DonorsApplicationsPage = Loadable(lazy(() => import('views/admin/donor-section/applications')));
const DonorCustomQuestionsPage = Loadable(lazy(() => import('views/admin/custom-questions/donor-custom-questions')));
const DonorUpdatePage = Loadable(lazy(() => import('views/admin/donor-section/update-user')));

//recipient
const RecipientsPage = Loadable(lazy(() => import('views/admin/recipient-section/view-users')));
const RecipientsApplicationsPage = Loadable(lazy(() => import('views/admin/recipient-section/applications')));
const RecipientCustomQuestionsPage = Loadable(lazy(() => import('views/admin/custom-questions/recipient-custom-questions')));
const RecipientUpdatePage = Loadable(lazy(() => import('views/admin/recipient-section/update-user')));

//delivery-driver
const DeliveryDriversPage = Loadable(lazy(() => import('views/admin/delivery-driver-section/view-users')));
const DeliveryDriverApplicationsPage = Loadable(lazy(() => import('views/admin/delivery-driver-section/applications')));
const DeliveryDriverCustomQuestionsPage = Loadable(lazy(() => import('views/admin/custom-questions/delivery-driver-custom-questions')));
const DeliveryDriverUpdatePage = Loadable(lazy(() => import('views/admin/delivery-driver-section/update-user')));

//regions
const RegionsPage = Loadable(lazy(() => import('views/admin/regions-section/view-regions')));
const RegionCreatePage = Loadable(lazy(() => import('views/admin/regions-section/create-region')));
const RegionEditPage = Loadable(lazy(() => import('views/admin/regions-section/edit-region')));

//messages
const AdminMessagesScreen = Loadable(lazy(() => import('views/admin/messages-section/admin-messages-screen')));

//integrations
const LGLIntegrationPage = Loadable(lazy(() => import('views/admin/integrations/lgl-integration-screen')));
const SharingExcessIntegrationPage = Loadable(lazy(() => import('views/admin/integrations/sharing-excess/sharing-excess-integration-screen')));

//update password
const UpdatePasswordPage = Loadable(lazy(() => import('views/shared/update-password')));

//update profile
const UpdateProfilePage = Loadable(lazy(() => import('views/admin/profile/AdminUpdateProfile')));

//two factor settings
const ConfigureTwoFactorSettingsPage = Loadable(lazy(() => import('views/shared/configure-2fa-settings/ConfigureTwoFactorSettings')));

//templates
const TemplateEditPage = Loadable(lazy(() => import('views/admin/reports-section/report-templates/edit-template')));
const TemplateViewPage = Loadable(lazy(() => import('views/admin/reports-section/report-templates/view-template')));
const TemplateCreatePage = Loadable(lazy(() => import('views/admin/reports-section/report-templates/create-template')));

// ==============================|| ADMIN ROUTING ||============================== //

const AdminRoutes = {
    path: '/',
    element: (
        <AuthGuard>
            <MainLayout />
        </AuthGuard>
    ),
    children: [
        {
            path: '/',
            element: <Dashboard />
        },
        {
            path: '/dashboard',
            element: <Dashboard />
        },
        {
            path: '/photos',
            element: <PhotosPage />
        },
        {
            path: '/metrics',
            element: <AdminMetricsPage />
        },
        {
            path: '/routes',
            element: <RoutesPage />
        },
        {
            path: '/archived-routes',
            element: <AdminArchivedRoutesScreen />
        },
        {
            path: '/routes/create',
            element: <RoutesCreatePage />
        },
        {
            path: '/routes/edit/:routeID',
            element: <RoutesEditPage />
        },
        {
            path: '/achievements',
            element: <AchievementsPage />
        },
        {
            path: '/achievements/:achievementId',
            element: <AchievementPage />
        },
        {
            path: '/achievements/create',
            element: <AchievementPage />
        },
        {
            path: '/prizes',
            element: <PrizesPage />
        },
        {
            path: '/prizes/:prizeId',
            element: <PrizePage />
        },
        {
            path: '/prizes/create',
            element: <PrizePage />
        },
        {
            path: '/organization',
            element: <OrganizationSettingsPage />
        },
        {
            path: '/administrators',
            element: <AdministratorsPage />
        },
        {
            path: '/archived-administrators',
            element: <AdminArchivedAdministratorScreen />
        },
        {
            path: '/administrator/update/:userID?',
            element: <AdministratorUpdatePage />
        },
        {
            path: '/pending-routes',
            element: <PendingRoutesPage />
        },
        {
            path: '/marketplace-routes',
            element: <MarketplaceRoutesPage />
        },
        {
            path: '/route-instances-by-date',
            element: <RouteInstancesByDatePage />
        },
        {
            path: '/donors',
            element: <DonorsPage />
        },
        {
            path: '/donors/applications',
            element: <DonorsApplicationsPage />
        },
        {
            path: '/donor/update/:userID?',
            element: <DonorUpdatePage />
        },
        {
            path: '/archived-donors',
            element: <AdminArchivedDonorScreen />
        },
        {
            path: '/donors/custom-questions',
            element: <DonorCustomQuestionsPage />
        },
        {
            path: '/recipients',
            element: <RecipientsPage />
        },
        {
            path: '/archived-recipients',
            element: <AdminArchivedRecipientScreen />
        },
        {
            path: '/recipients/custom-questions',
            element: <RecipientCustomQuestionsPage />
        },
        {
            path: '/recipients/applications',
            element: <RecipientsApplicationsPage />
        },
        {
            path: '/recipient/update/:userID?',
            element: <RecipientUpdatePage />
        },
        {
            path: '/delivery-drivers',
            element: <DeliveryDriversPage />
        },
        {
            path: '/archived-delivery-drivers',
            element: <AdminArchivedDeliveryDriverScreen />
        },
        {
            path: '/delivery-drivers/applications',
            element: <DeliveryDriverApplicationsPage />
        },
        {
            path: '/delivery-drivers/custom-questions',
            element: <DeliveryDriverCustomQuestionsPage />
        },
        {
            path: '/delivery-driver/update/:userID?',
            element: <DeliveryDriverUpdatePage />
        },
        {
            path: '/report/route',
            element: <RouteReportPage />
        },
        {
            path: '/report/profile',
            element: <ProfileReportPage />
        },
        {
            path: '/report/templates',
            element: <TemplatesPage />
        },
        {
            path: '/report/templates/view/:templateID',
            element: <TemplateViewPage />
        },
        {
            path: '/report/templates/:templateID',
            element: <TemplateEditPage />
        },
        {
            path: '/report/templates/create',
            element: <TemplateCreatePage />
        },
        {
            path: '/report/templates/help',
            element: <TemplateHelpPage />
        },
        {
            path: '/regions',
            element: <RegionsPage />
        },
        {
            path: '/region/create',
            element: <RegionCreatePage />
        },
        {
            path: '/region/edit/:regionID',
            element: <RegionEditPage />
        },
        {
            path: '/2fa-settings',
            element: <ConfigureTwoFactorSettingsPage />
        },
        {
            path: '/user/update-password',
            element: <UpdatePasswordPage />
        },
        {
            path: '/user/update-profile',
            element: <UpdateProfilePage />
        },
        {
            path: '/programs',
            element: <ProgramsPage />
        },
        {
            path: '/programs/edit/:programID',
            element: <ProgramPage />
        },
        {
            path: '/programs/create',
            element: <ProgramPage />
        },
        {
            path: '/archivedPrograms',
            element: <ArchivedProgramsPage />
        },
        {
            path: '/food-products',
            element: <FoodProductsPage />
        },
        {
            path: '/archived-food-products',
            element: <ArchivedFoodProductsPage />
        },
        {
            path: '/messages/:conversationId?',
            element: (
                <>
                    <MessagingGuard>
                        <AdminMessagesScreen />
                    </MessagingGuard>
                </>
            )
        },
        {
            path: '/lgl-integration',
            element: <LGLIntegrationPage />
        },
        {
            path: '/sharing-excess-integration',
            element: <SharingExcessIntegrationPage />
        },
    ]
};

export default AdminRoutes;
